import React, { useState, useEffect, useRef, useMemo } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { createMapInstance } from '../../utils/maplibreConfig';

const DEFAULT_POSITION = [48.8566, 2.3522]; // [lat, lng]

/** Calcul de distance haversine en mètres entre deux [lat, lng] */
function haversineDistance([lat1, lng1], [lat2, lng2]) {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function createRouteEndpointMarkerElement({ accentColor, innerColor, shadowColor }) {
  const element = document.createElement('div');
  element.style.cssText = `
    position: relative;
    width: 30px;
    height: 42px;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    cursor: pointer;
  `;

  const halo = document.createElement('div');
  halo.style.cssText = `
    position: absolute;
    left: 50%;
    bottom: 3px;
    width: 20px;
    height: 12px;
    background: ${shadowColor};
    border-radius: 999px;
    filter: blur(6px);
    transform: translateX(-50%);
    pointer-events: none;
  `;

  const pin = document.createElement('div');
  pin.style.cssText = `
    position: relative;
    width: 28px;
    height: 40px;
    background: linear-gradient(180deg, ${accentColor} 0%, #0f172a 100%);
    clip-path: path('M14 0C6.268 0 0 6.268 0 14c0 10.42 12.208 23.633 13.636 25.154a.5.5 0 0 0 .728 0C15.792 37.633 28 24.42 28 14 28 6.268 21.732 0 14 0Z');
    box-shadow: 0 10px 24px rgba(15,23,42,0.24);
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
  `;

  const dot = document.createElement('div');
  dot.style.cssText = `
    position: absolute;
    top: 9px;
    left: 50%;
    width: 9px;
    height: 9px;
    border-radius: 999px;
    background: ${innerColor};
    transform: translateX(-50%);
  `;

  pin.appendChild(dot);
  element.appendChild(halo);
  element.appendChild(pin);
  return element;
}

export default function RideShareMap({ 
  startPosition, 
  endPosition, 
  onStartChange, 
  onEndChange,
  height = 400,
  readOnly = false
}) {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const startMarkerRef = useRef(null);
  const endMarkerRef = useRef(null);
  const [routeCoordinates, setRouteCoordinates] = useState(null);
  const [isRouting, setIsRouting] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const hasFittedRef = useRef(false);

  // Normalisation [lat, lng]
  const startPos = (startPosition && startPosition[0] && startPosition[1])
    ? [Number(startPosition[0]), Number(startPosition[1])]
    : null;
  const endPos = (endPosition && endPosition[0] && endPosition[1])
    ? [Number(endPosition[0]), Number(endPosition[1])]
    : null;

  const initialCenter = startPos || endPos || DEFAULT_POSITION;

  // ── Initialisation de la carte ──────────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current || mapInstanceRef.current) return;

    const [lat, lng] = initialCenter;
    const map = createMapInstance({
      container: containerRef.current,
      center: [lng, lat],
      zoom: 13,
      pitch: 0,
    });

    map.on('load', () => {
      setMapLoaded(true);
      // Source GeoJSON pour l'itinéraire
      map.addSource('route', {
        type: 'geojson',
        data: { type: 'Feature', geometry: { type: 'LineString', coordinates: [] }, properties: {} },
      });
      map.addLayer({
        id: 'route-line',
        type: 'line',
        source: 'route',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { 'line-color': '#6200ee', 'line-width': 5, 'line-opacity': 1 },
      });
    });

    // Clic sur la carte pour placer les marqueurs
    map.on('click', (e) => {
      if (readOnly) return;
      const clickedLat = e.lngLat.lat;
      const clickedLng = e.lngLat.lng;

      if (!startPos) {
        onStartChange?.({ latitude: clickedLat, longitude: clickedLng });
      } else if (!endPos) {
        onEndChange?.({ latitude: clickedLat, longitude: clickedLng });
      } else {
        // Déplacer le point le plus proche
        const distStart = haversineDistance(startPos, [clickedLat, clickedLng]);
        const distEnd = haversineDistance(endPos, [clickedLat, clickedLng]);
        if (distStart < distEnd) {
          onStartChange?.({ latitude: clickedLat, longitude: clickedLng });
        } else {
          onEndChange?.({ latitude: clickedLat, longitude: clickedLng });
        }
      }
    });

    mapInstanceRef.current = map;

    return () => {
      map.remove();
      mapInstanceRef.current = null;
      startMarkerRef.current = null;
      endMarkerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Marqueur départ ───────────────────────────────────────────────────────
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    if (!startPos) {
      startMarkerRef.current?.remove();
      startMarkerRef.current = null;
      return;
    }

    const [lat, lng] = startPos;
    if (!startMarkerRef.current) {
      const el = createRouteEndpointMarkerElement({
        accentColor: '#0af216',
        innerColor: '#e8f5e9',
        shadowColor: 'rgba(46,125,50,0.24)',
      });
      const marker = new maplibregl.Marker({ element: el, anchor: 'bottom', draggable: !readOnly })
        .setLngLat([lng, lat])
        .setPopup(
          new maplibregl.Popup({ offset: 28 }).setText(
            readOnly ? t('listing.map.departure', 'Départ') : t('listing.map.departurePoint', 'Point de départ (Glissez pour modifier)')
          )
        )
        .addTo(map);

      marker.on('dragend', () => {
        if (readOnly) return;
        const lngLat = marker.getLngLat();
        onStartChange?.({ latitude: lngLat.lat, longitude: lngLat.lng });
      });

      el.addEventListener('click', (e) => e.stopPropagation());
      startMarkerRef.current = marker;
    } else {
      startMarkerRef.current.setLngLat([lng, lat]);
    }
  }, [startPos?.[0], startPos?.[1], readOnly]);

  // ── Marqueur arrivée ──────────────────────────────────────────────────────
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    if (!endPos) {
      endMarkerRef.current?.remove();
      endMarkerRef.current = null;
      return;
    }

    const [lat, lng] = endPos;
    if (!endMarkerRef.current) {
      const el = createRouteEndpointMarkerElement({
        accentColor: '#f60707',
        innerColor: '#ffebee',
        shadowColor: 'rgba(211,47,47,0.24)',
      });
      const marker = new maplibregl.Marker({ element: el, anchor: 'bottom', draggable: !readOnly })
        .setLngLat([lng, lat])
        .setPopup(
          new maplibregl.Popup({ offset: 28 }).setText(
            readOnly ? t('listing.map.arrival', 'Arrivée') : t('listing.map.arrivalPoint', "Point d'arrivée (Glissez pour modifier)")
          )
        )
        .addTo(map);

      marker.on('dragend', () => {
        if (readOnly) return;
        const lngLat = marker.getLngLat();
        onEndChange?.({ latitude: lngLat.lat, longitude: lngLat.lng });
      });

      el.addEventListener('click', (e) => e.stopPropagation());
      endMarkerRef.current = marker;
    } else {
      endMarkerRef.current.setLngLat([lng, lat]);
    }
  }, [endPos?.[0], endPos?.[1], readOnly]);

  // ── Récupération de l'itinéraire OSRM ────────────────────────────────────
  useEffect(() => {
    if (!startPos || !endPos) {
      setRouteCoordinates(null);
      return;
    }

    setIsRouting(true);
    // OSRM attend {lon},{lat}
    const startCoord = `${startPos[1]},${startPos[0]}`;
    const endCoord = `${endPos[1]},${endPos[0]}`;
    const url = `https://router.project-osrm.org/route/v1/driving/${startCoord};${endCoord}?overview=full&geometries=geojson`;

    fetch(url)
      .then((r) => r.json())
      .then((data) => {
        if (data?.routes?.length > 0) {
          // OSRM peut "snapper" légèrement les extrémités sur le réseau routier.
          // On remplace donc explicitement le premier et le dernier point par les
          // coordonnées exactes sélectionnées afin que le tracé touche visuellement
          // les marqueurs de départ et d'arrivée.
          const routeGeometry = data.routes[0].geometry.coordinates || [];
          const coords = routeGeometry.map((coord) => [coord[0], coord[1]]);

          if (coords.length > 0) {
            coords[0] = [startPos[1], startPos[0]];
            coords[coords.length - 1] = [endPos[1], endPos[0]];
          }

          setRouteCoordinates(coords);
        } else {
          setRouteCoordinates(null);
        }
      })
      .catch(() => setRouteCoordinates(null))
      .finally(() => setIsRouting(false));
  }, [startPos?.[0], startPos?.[1], endPos?.[0], endPos?.[1]]);

  // ── Mise à jour du layer route ───────────────────────────────────────────
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapLoaded || !map.getSource('route')) return;

    let coords;
    if (routeCoordinates) {
      coords = routeCoordinates;
    } else if (startPos && endPos) {
      // Ligne directe : MapLibre veut [lng, lat]
      coords = [[startPos[1], startPos[0]], [endPos[1], endPos[0]]];
    } else {
      coords = [];
    }

    map.getSource('route').setData({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: coords },
      properties: {},
    });
  }, [routeCoordinates, startPos?.[0], startPos?.[1], endPos?.[0], endPos?.[1], mapLoaded]);

  // ── Ajustement initial de la vue (départ + arrivée) ──────────────────────
  // Ne s'exécute qu'une seule fois au chargement : évite que la carte
  // re-zoome à chaque déplacement d'un marqueur.
  useEffect(() => {
    if (!mapLoaded || hasFittedRef.current || !startPos || !endPos) return;
    const map = mapInstanceRef.current;
    if (!map) return;
    hasFittedRef.current = true;
    try {
      map.fitBounds(
        [[Math.min(startPos[1], endPos[1]), Math.min(startPos[0], endPos[0])],
         [Math.max(startPos[1], endPos[1]), Math.max(startPos[0], endPos[0])]],
        { padding: 50, maxZoom: 15 }
      );
    } catch (_) {}
  }, [mapLoaded, startPos?.[0], startPos?.[1], endPos?.[0], endPos?.[1]]);

  // ── Vue initiale readOnly (marqueur seul sans arrivée) ───────────────────
  useEffect(() => {
    if (!readOnly || !mapLoaded || !startPos || endPos) return;
    const map = mapInstanceRef.current;
    if (!map) return;
    map.flyTo({ center: [startPos[1], startPos[0]], zoom: 13 });
  }, [readOnly, mapLoaded, startPos?.[0], startPos?.[1], endPos?.[0], endPos?.[1]]);

  return (
    <Box sx={{ position: 'relative' }}>
      <div
        ref={containerRef}
        style={{ height, width: '100%', borderRadius: '12px', overflow: 'hidden' }}
      />

      <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center', gap: 2 }}>
        <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box component="span" sx={{ width: 10, height: 10, bgcolor: '#2e7d32', borderRadius: '50%', display: 'inline-block' }} />
          {t('listing.map.greenMarker', 'Départ')}
        </Typography>
        <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box component="span" sx={{ width: 10, height: 10, bgcolor: '#d32f2f', borderRadius: '50%', display: 'inline-block' }} />
          {t('listing.map.redMarker', 'Arrivée')}
        </Typography>
        {isRouting && (
          <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
            Calcul de l'itinéraire...
          </Typography>
        )}
      </Box>
      <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 0.5, color: 'text.secondary', fontStyle: 'italic' }}>
        {t('listing.map.dragHint', 'Astuce : Vous pouvez glisser-déposer les marqueurs pour ajuster précisément votre trajet.')}
      </Typography>
    </Box>
  );
}
